import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const FIELDS = [
  { key: "password_min_length", label: "Minimum password length", type: "number", group: "password" },
  { key: "require_uppercase", label: "Require uppercase letter", type: "bool", group: "password" },
  { key: "require_lowercase", label: "Require lowercase letter", type: "bool", group: "password" },
  { key: "require_digit", label: "Require digit", type: "bool", group: "password" },
  { key: "require_symbol", label: "Require symbol", type: "bool", group: "password" },
  { key: "password_expiry_days", label: "Password expiry (days, 0 = never)", type: "number", group: "password" },
  { key: "password_history_count", label: "Disallow last N passwords", type: "number", group: "password" },
  { key: "max_failed_attempts", label: "Max failed login attempts", type: "number", group: "session" },
  { key: "lockout_minutes", label: "Account lockout duration (minutes)", type: "number", group: "session" },
  { key: "idle_timeout_minutes", label: "Idle session timeout (minutes, 0 = disabled)", type: "number", group: "session" },
  { key: "session_lifetime_hours", label: "Absolute session lifetime (hours)", type: "number", group: "session" },
];

export default function Security() {
  const [pol, setPol] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/security/policy").then(({ data }) => setPol(data));
  }, []);

  const update = (key, value) => setPol((p) => ({ ...p, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => { payload[f.key] = pol[f.key]; });
      const { data } = await api.put("/security/policy", payload);
      setPol(data);
      toast.success("Security policy updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!pol) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="security-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Security controls</h1>
        <p className="text-muted-foreground">Password policy, login lockout, idle session timeout and absolute session lifetime — all enforced per tenant.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Password policy</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {FIELDS.filter((f) => f.group === "password").map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4">
              <Label className="text-sm">{f.label}</Label>
              {f.type === "bool" ? (
                <Switch checked={!!pol[f.key]} onCheckedChange={(v) => update(f.key, v)} data-testid={`pol-${f.key}`} />
              ) : (
                <Input type="number" value={pol[f.key] ?? 0} onChange={(e) => update(f.key, parseInt(e.target.value || "0", 10))} className="w-32" data-testid={`pol-${f.key}`} />
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Session & lockout</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {FIELDS.filter((f) => f.group === "session").map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4">
              <Label className="text-sm">{f.label}</Label>
              <Input type="number" value={pol[f.key] ?? 0} onChange={(e) => update(f.key, parseInt(e.target.value || "0", 10))} className="w-32" data-testid={`pol-${f.key}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-policy">{saving ? "Saving…" : "Save policy"}</Button>
      </div>
    </div>
  );
}
